import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom'
import validation from 'react-validation-mixin'
import strategy from 'react-validatorjs-strategy'

import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  addShortlistSection,
  removeShortlistSection,
  updateShortlistSection,
} from '@disco/state-management/actions/ShortlistActions'
import { getLocationBeforeTransitions } from '@disco/state-management/selectors/routing'
import { getShortlistById } from '@disco/state-management/selectors/shortlists'

import type { Shortlist } from 'types/shortlist'

import { getShortlistBaseRoute } from 'utils/routing'

import DocumentHeadTags from 'components/document-head-tags'
import { RadioButton, RadioGroup } from 'components/ui'
import ModalDialog from 'components/ui/modal-dialog'
import Stack from 'components/ui/stack'
import TextField from 'components/ui/text-field'

import type { RootState } from 'store/configureStore'

const mapStateToProps = (
  state: RootState,
  { params: { shortlistId } }: { params: { shortlistId?: string } },
) => {
  return {
    shortlist: getShortlistById(state, {
      id: shortlistId,
    }),
    location: getLocationBeforeTransitions(state),
  }
}

type Props = {
  updateShortlistSection: (...args: Array<any>) => any
  navigate: NavigateFunction
  validate: (...args: Array<any>) => any
  addShortlistSection: typeof addShortlistSection
  sendMixpanelEvent: typeof sendMixpanelEvent
  removeShortlistSection: typeof removeShortlistSection
  getValidationMessages: (...args: Array<any>) => any
  shortlist: Shortlist
  location: any
  params: {
    shortlistId: string
  }
}
type State = {
  name: string
  targetPosition: 'top' | 'bottom'
}
const POSITION_OPTIONS = [
  {
    value: 'top',
    label: 'Insert at the top of the playlist',
  },
  {
    value: 'bottom',
    label: 'Insert at the bottom of the playlist',
  },
]

class EditSection extends React.Component<Props, State> {
  validatorTypes = strategy.createSchema(
    {
      name: 'required|string|between:0,255',
    },
    {
      'between.name': 'Sorry! That section title is too long.',
      'required.name': 'Name is required',
    },
  )
  state: State = {
    targetPosition: 'top',
    name: '',
  }
  _onPositionChange = (value: 'top' | 'bottom') => {
    this.setState({
      targetPosition: value,
    })
  }
  _onClose = () => {
    const baseRoute = getShortlistBaseRoute(this.props.location)
    const returnPath = baseRoute || '/'
    this.props.navigate(returnPath)
  }
  _onNameChange = e =>
    this.setState({
      name: e.target.value,
    })
  _onSubmit = () => {
    this.props.validate(error => {
      if (error) return
      const { shortlistId } = this.props.params
      const { name } = this.state
      this.props.addShortlistSection(
        shortlistId,
        name,
        this.state.targetPosition === 'top'
          ? 0
          : this.props.shortlist.items.length,
        this.state.targetPosition,
      )
      this.props.sendMixpanelEvent(
        this.state.targetPosition === 'top'
          ? 'Playlist Creator - Insert section at top of playlist '
          : 'Playlist Creator - Insert section at bottom of playlist',
      )

      this._onClose()
    })
  }
  getValidatorData = () => {
    return {
      name: this.state.name,
    }
  }

  render() {
    const nameMessage = this.props.getValidationMessages('name').toString()
    return (
      <>
        <DocumentHeadTags title="Add section | DISCO" />
        <ModalDialog
          title="New section"
          primaryAction={{
            caption: 'Create section',
            onClick: this._onSubmit,
          }}
          onCancel={this._onClose}
        >
          <Stack space="small">
            <TextField
              autoFocus
              error={nameMessage}
              invalid={Boolean(nameMessage)}
              label="Section name"
              placeholder="Name your section"
              value={this.state.name}
              onChange={this._onNameChange}
            />
            <RadioGroup
              value={this.state.targetPosition}
              onChange={this._onPositionChange}
            >
              {POSITION_OPTIONS.map(option => (
                <RadioButton
                  key={option.value}
                  label={option.label}
                  value={option.value}
                />
              ))}
            </RadioGroup>
          </Stack>
        </ModalDialog>
      </>
    )
  }
}

const mapDispatchToProps = {
  addShortlistSection,
  updateShortlistSection,
  removeShortlistSection,
  sendMixpanelEvent,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

const ValidatingComponent = validation(strategy)(EditSection)

const ConnectedEditSection = connector(ValidatingComponent)

export default function EditSectionWithNavigation(
  props: Partial<PropsFromRedux>,
) {
  const navigate = useNavigate()
  const params = useParams<{ shortlistId: string }>()
  return <ConnectedEditSection {...props} navigate={navigate} params={params} />
}

type PropsFromRedux = ConnectedProps<typeof connector>
